import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  FormHelperText,
  keyframes, // Add this import
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { register } from '../queries/register';
import { login } from '../queries/login';
import { useAuthContext } from '../context/AuthContext';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { usePersistentNavigate } from '../utils/navigation';

// Define keyframes for pulsing animation - same as in CaseReport
const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

// Define keyframes for the circular animation - same as in CaseReport
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const RegisterContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  padding: '16px',
});

const RegisterCard = styled(Paper)({
  padding: '40px',
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '8px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
});

const Register: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordStep, setIsPasswordStep] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [emailError, setEmailError] = useState(''); // New state for email error
  const { login } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (confirmPassword) {
      setConfirmPasswordTouched(true);
      setPasswordsMatch(password === confirmPassword);
    } else {
      setPasswordsMatch(true);
    }
  }, [password, confirmPassword]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordStep) {
      // First step - email entered, now show password fields
      setEmailError(''); // Clear any previous email errors
      setIsPasswordStep(true);
    } else {
      // Second step - email and passwords entered
      setIsLoading(true); // Start loading animation
      try {
        // Register the user
        const response = await register({
          email,
          password
        });

        login({
          accessToken: response.accessToken,
          acceptedTermsOfService: false,
          registrationComplete: false,
        })

        navigate('/complete-registration');
      } catch (error) {
        console.error("Registration failed:", error);
        setIsLoading(false); // Stop loading on error

        // Check for duplicate email (409 Conflict)
        if ((error as any).toString().includes("409")) {
          setIsPasswordStep(false); // Reset to email input step
          setPassword(''); // Clear password fields
          setConfirmPassword('');
          setEmailError('Email already registered.'); // Set error message for email
        }
      }
    }
  };

  const handleBackToEmail = () => {
    setIsPasswordStep(false);
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError('');
    }
  };

  return (
    <RegisterContainer maxWidth={false}>
      <RegisterCard elevation={0}>
        {/* PrecisCa AI Logo/Title as a link back to landing page */}
        <RouterLink
          to="/"
          style={{ textDecoration: 'none' }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 300,
              mb: 2,
              fontSize: { xs: '2rem', sm: '3rem' },
              display: 'flex',
              alignItems: 'center',
              color: '#2c5282',
              textAlign: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <Box component="span">PrecisCa AI</Box>
            <Box
              component="span"
              sx={{
                color: '#4299e1',
                fontSize: { xs: '0.8rem', sm: '1.2rem' },
                verticalAlign: 'super',
                ml: 1
              }}
            >
              ®
            </Box>
          </Typography>
        </RouterLink>

        {/* Loading spinner - only show when isLoading is true */}
        {isLoading ? (
          <Box sx={{ my: 8 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 180,
                height: 180
              }}
            >
              {/* SVG Circle Animation */}
              <Box sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                animation: `${rotate} 2s linear infinite`
              }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                    stroke="#4299e1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeDasharray="283"
                    strokeDashoffset="283"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="283"
                      to="0"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              </Box>

              {/* Pulsing Text */}
              <Typography
                variant="h4"
                sx={{
                  color: '#2c5282',
                  fontWeight: '500',
                  animation: `${pulse} 2s ease-in-out infinite`,
                  fontSize: { xs: '1.1rem', sm: '1.4rem' }, // Reduced from 1.3rem/1.7rem
                  transition: 'opacity 0.5s ease',
                  minWidth: '140px',
                  textAlign: 'center',
                  whiteSpace: 'pre-line'
                }}
              >
                Creating{'\n'}Account
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography
              variant="body1"
              sx={{
                mb: 1,
                mt: 3,
                color: '#4a5568',
                textAlign: 'center'
              }}
            >
              {isPasswordStep
                ? "Enter a password to use PrecisCa AI."
                : "Please register to access PrecisCa AI."}
            </Typography>

            {/* Form */}
            <Box
              component="form"
              onSubmit={handleContinue}
              sx={{ width: '100%' }}
            >
              {/* Email Field - always shown */}
              <TextField
                fullWidth
                id="email"
                label="Email address"
                variant="outlined"
                type="email"
                value={email}
                onChange={handleEmailChange}
                margin="normal"
                required
                error={!!emailError}
                disabled={isPasswordStep}
                InputLabelProps={{ required: false }}
                sx={{
                  mb: emailError ? 0.5 : 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: emailError ? '#f56565' : '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: emailError ? '#f56565' : '#cbd5e0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: emailError ? '#f56565' : '#4299e1',
                    },
                  },
                }}
              />
              {emailError && (
                <FormHelperText error sx={{ ml: 1.5, mt: -0.5, mb: 1 }}>
                  {emailError}
                </FormHelperText>
              )}

              {/* Password Fields - shown only on second step */}
              {isPasswordStep && (
                <>
                  <TextField
                    fullWidth
                    id="password"
                    label="Password"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                    autoFocus
                    InputLabelProps={{ required: false }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handlePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#e2e8f0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4299e1',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    id="confirmPassword"
                    label="Confirm password"
                    variant="outlined"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => {
                      if (confirmPassword) setConfirmPasswordTouched(true);
                    }}
                    margin="normal"
                    required
                    error={confirmPasswordTouched && !passwordsMatch}
                    InputLabelProps={{ required: false }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleConfirmPasswordVisibility}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: !passwordsMatch && confirmPasswordTouched ? 0.5 : 1,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: confirmPasswordTouched && !passwordsMatch ? '#f56565' : '#e2e8f0',
                        },
                        '&:hover fieldset': {
                          borderColor: confirmPasswordTouched && !passwordsMatch ? '#f56565' : '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: confirmPasswordTouched && !passwordsMatch ? '#f56565' : '#4299e1',
                        },
                      },
                    }}
                  />
                  {confirmPasswordTouched && !passwordsMatch && (
                    <FormHelperText error sx={{ ml: 1.5, mt: 0, mb: 1 }}>
                      Passwords don't match
                    </FormHelperText>
                  )}
                </>
              )}

              {/* Continue Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={(isPasswordStep && (!passwordsMatch || !password || !confirmPassword)) || isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  bgcolor: '#4299e1',
                  '&:hover': {
                    bgcolor: '#2c5282',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#cbd5e0',
                  },
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 'none',
                  borderRadius: '4px',
                }}
              >
                Continue
              </Button>

              {/* Different links/buttons based on step */}
              {isPasswordStep ? (
                <>
                  {/* "Use different email" button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleBackToEmail}
                    disabled={isLoading}
                    sx={{
                      mb: 4,
                      py: 1.5,
                      color: '#718096',
                      borderColor: '#e2e8f0',
                      backgroundColor: '#f7fafc',
                      '&:hover': {
                        backgroundColor: '#edf2f7',
                        borderColor: '#cbd5e0',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.7,
                      },
                      fontWeight: 500,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: 'none',
                      borderRadius: '4px',
                    }}
                  >
                    Use a different email
                  </Button>
                </>
              ) : (
                <>
                  {/* Already have an account text */}
                  <Typography variant="body2" sx={{ mb: 4, color: '#4a5568', textAlign: 'center' }}>
                    Already have an account?{' '}
                    <RouterLink
                      to="/login"
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: '#4299e1',
                          fontWeight: 500,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Log in
                      </Typography>
                    </RouterLink>
                  </Typography>

                  {/* OR Divider */}
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', margin: '24px 0' }}>
                    <Box sx={{ flexGrow: 1, borderBottom: '1px solid #e2e8f0' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        px: 2,
                        color: '#718096',
                        fontWeight: 500,
                      }}
                    >
                      OR
                    </Typography>
                    <Box sx={{ flexGrow: 1, borderBottom: '1px solid #e2e8f0' }} />
                  </Box>

                  {/* Google SSO Button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GoogleIcon />}
                    disabled
                    sx={{
                      mt: 1,
                      py: 1.2,
                      color: '#4a5568',
                      borderColor: '#e2e8f0',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      fontWeight: 400,
                      '&:hover': {
                        borderColor: '#cbd5e0',
                        bgcolor: '#f7fafc',
                      },
                    }}
                  >
                    Continue with Google <Typography component="span" sx={{ ml: 1, color: '#718096', fontSize: '0.85rem' }}>(Coming Soon)</Typography>
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;